import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, AlertTriangle, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { importSubscribers } from '../../lib/campaignsApi';

const REQUIRED = ['company', 'contact_name', 'email', 'country', 'segment'];
const TEMPLATE_CSV =
  'company,contact_name,email,country,segment\n' +
  'ACME Distribuidora,Juan García,juan@acme.com,Mexico,Reseller\n' +
  'TecnoChile,María López,maria@tecnochile.cl,Chile,Reseller\n';

// Minimal RFC-4180-ish CSV parser (handles quotes, commas, escaped quotes, CRLF)
const parseCSV = (text) => {
  const rows = [];
  let cur = ['']; let i = 0; let inQuotes = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i += 2; continue; }
      if (ch === '"') { inQuotes = false; i++; continue; }
      cur[cur.length - 1] += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ',') { cur.push(''); i++; continue; }
    if (ch === '\r') { i++; continue; }
    if (ch === '\n') { rows.push(cur); cur = ['']; i++; continue; }
    cur[cur.length - 1] += ch; i++;
  }
  if (cur.length > 1 || cur[0] !== '') rows.push(cur);
  return rows;
};

const normalizeHeader = (h) => (h || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const HEADER_ALIASES = {
  company: 'company',
  company_name: 'company',
  contact_name: 'contact_name',
  name: 'contact_name',
  contact: 'contact_name',
  email: 'email',
  sales_email: 'email',
  country: 'country',
  segment: 'segment',
  company_type: 'segment',
  type: 'segment',
};

const downloadTemplate = () => {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'luxortek-subscribers-import-template.csv'; a.click();
  URL.revokeObjectURL(url);
};

export const SubscriberImportModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { headers, rows, missingHeaders, invalidRows, validRows }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error('Please choose a .csv file');
      return;
    }
    const text = await f.text();
    const arr = parseCSV(text).filter((r) => r.some((c) => c && c.trim() !== ''));
    if (arr.length < 2) {
      toast.error('CSV must have a header row and at least one data row');
      return;
    }
    const rawHeaders = arr[0].map(normalizeHeader);
    const headerMap = rawHeaders.map((h) => HEADER_ALIASES[h] || h);
    const missing = REQUIRED.filter((req) => !headerMap.includes(req));
    const indices = REQUIRED.map((req) => headerMap.indexOf(req));

    const dataRows = arr.slice(1).map((row) => ({
      company: indices[0] >= 0 ? (row[indices[0]] || '').trim() : '',
      contact_name: indices[1] >= 0 ? (row[indices[1]] || '').trim() : '',
      email: indices[2] >= 0 ? (row[indices[2]] || '').trim() : '',
      country: indices[3] >= 0 ? (row[indices[3]] || '').trim() : '',
      segment: indices[4] >= 0 ? (row[indices[4]] || '').trim() : '',
    }));

    const isValidRow = (r) =>
      r.company && r.contact_name && r.email && r.country && r.segment &&
      /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email);
    const validRows = dataRows.filter(isValidRow);

    setFile(f);
    setParsed({
      detectedHeaders: rawHeaders,
      missingHeaders: missing,
      totalRows: dataRows.length,
      validRows,
      invalidRows: dataRows.length - validRows.length,
      preview: dataRows.slice(0, 5),
    });
    setResult(null);
  };

  const runImport = async () => {
    if (!parsed || parsed.validRows.length === 0) return;
    try {
      setSubmitting(true);
      const r = await importSubscribers(parsed.validRows);
      setResult(r.data);
      const { imported, skipped_duplicates, invalid_rows } = r.data;
      const msg = `Imported ${imported}, skipped ${skipped_duplicates} duplicate${skipped_duplicates === 1 ? '' : 's'}` +
        (invalid_rows ? `, ${invalid_rows} invalid` : '');
      toast.success(msg);
      onImported && onImported(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="import-subscribers-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-[640px] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="text-white font-semibold flex items-center gap-2">
            <Upload className="w-4 h-4 text-[#00D4FF]" />
            Import Subscribers from CSV
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="import-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Format help */}
          <div className="border border-white/10 bg-[#050505] p-3 text-xs text-white/65" data-testid="import-format-help">
            <div className="font-semibold text-white/85 mb-1">Required CSV columns</div>
            <div className="font-mono text-[11px] text-[#00D4FF]">company, contact_name, email, country, segment</div>
            <div className="mt-2 text-white/45">
              Segment must be one of: Reseller, Corporate, Government, Education, Exporter, Other (case-insensitive — unknown values become "Other"). Duplicates by email are skipped automatically.
            </div>
            <button onClick={downloadTemplate} className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#00D4FF] hover:underline" data-testid="import-template-btn">
              <Download className="w-3 h-3" /> Download template CSV
            </button>
          </div>

          {/* File picker */}
          {!result && (
            <div>
              <label
                htmlFor="csv-file-input"
                className="block border border-dashed border-white/15 hover:border-[#00D4FF]/50 bg-[#050505] p-6 text-center cursor-pointer transition-colors"
                data-testid="import-file-dropzone"
              >
                <Upload className="w-6 h-6 mx-auto text-white/40 mb-2" />
                <div className="text-white/75 text-sm">{file ? file.name : 'Click to choose a CSV file'}</div>
                <div className="text-white/40 text-[11px] mt-1">UTF-8 encoded · max 10,000 rows recommended</div>
              </label>
              <input
                id="csv-file-input"
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="import-file-input"
              />
            </div>
          )}

          {/* Parse preview */}
          {parsed && !result && (
            <div className="space-y-3" data-testid="import-preview">
              {parsed.missingHeaders.length > 0 ? (
                <div className="border border-red-400/30 bg-red-400/5 p-3 text-red-200/90 text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Missing required column{parsed.missingHeaders.length === 1 ? '' : 's'}: <span className="font-mono text-red-100">{parsed.missingHeaders.join(', ')}</span>
                    <div className="text-red-200/60 mt-1">Detected: <span className="font-mono">{parsed.detectedHeaders.join(', ')}</span></div>
                  </div>
                </div>
              ) : (
                <div className="border border-emerald-400/20 bg-emerald-400/5 p-3 text-emerald-200/90 text-xs flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Parsed <span className="font-mono">{parsed.totalRows}</span> row{parsed.totalRows === 1 ? '' : 's'} — <span className="font-mono">{parsed.validRows.length}</span> valid, <span className="font-mono">{parsed.invalidRows}</span> invalid.
                  </div>
                </div>
              )}

              {parsed.preview.length > 0 && (
                <div className="border border-white/10 overflow-x-auto">
                  <table className="w-full text-xs" data-testid="import-preview-table">
                    <thead className="bg-[#050505] text-white/55 uppercase tracking-wider text-[10px]">
                      <tr>
                        {REQUIRED.map((c) => <th key={c} className="text-left px-2 py-1.5">{c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.preview.map((r, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-2 py-1.5 text-white/85">{r.company || '—'}</td>
                          <td className="px-2 py-1.5 text-white/85">{r.contact_name || '—'}</td>
                          <td className="px-2 py-1.5 text-[#00D4FF]">{r.email || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.country || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.segment || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.totalRows > 5 && (
                    <div className="px-2 py-1.5 text-[11px] text-white/40 bg-[#050505] border-t border-white/5">…and {parsed.totalRows - 5} more rows</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-2" data-testid="import-result">
              <div className="border border-emerald-400/20 bg-emerald-400/5 p-3 text-emerald-200/90 text-sm flex items-start gap-2">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Import complete</div>
                  <ul className="text-xs space-y-0.5 list-disc list-inside text-emerald-200/80">
                    <li><span data-testid="import-result-imported">{result.imported}</span> new subscribers added</li>
                    <li><span data-testid="import-result-skipped">{result.skipped_duplicates}</span> duplicate{result.skipped_duplicates === 1 ? '' : 's'} skipped</li>
                    <li><span data-testid="import-result-invalid">{result.invalid_rows}</span> invalid row{result.invalid_rows === 1 ? '' : 's'} rejected</li>
                  </ul>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <details className="border border-amber-400/20 bg-amber-400/5 p-2 text-amber-200/85 text-xs">
                  <summary className="cursor-pointer">{result.errors.length} validation error{result.errors.length === 1 ? '' : 's'}</summary>
                  <ul className="mt-2 space-y-0.5 max-h-40 overflow-auto font-mono text-[11px]">
                    {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2">
          {result ? (
            <button onClick={onClose} className="btn-primary text-sm" data-testid="import-done-btn">Done</button>
          ) : (
            <>
              <button onClick={onClose} className="btn-secondary text-sm" data-testid="import-cancel-btn">Cancel</button>
              <button
                onClick={runImport}
                disabled={!parsed || parsed.missingHeaders.length > 0 || parsed.validRows.length === 0 || submitting}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="import-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Import {parsed?.validRows?.length || 0} row{(parsed?.validRows?.length || 0) === 1 ? '' : 's'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriberImportModal;
